import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useLanguage } from '@/context/LanguageContext';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Portfolio holdings data
const holdings = [
  { id: 1, name: 'Bitcoin', symbol: 'BTC', icon: 'fa-brands fa-bitcoin', iconColor: 'text-crypto-bitcoin bg-crypto-bitcoin/15',
    amount: 1.5234, value: 102456.78, avgBuy: 58234.00, currentPrice: 67234.56, change24h: 2.34, allocation: 45 },
  { id: 2, name: 'Ethereum', symbol: 'ETH', icon: 'fa-brands fa-ethereum', iconColor: 'text-crypto-ethereum bg-crypto-ethereum/15',
    amount: 12.456, value: 40456.23, avgBuy: 2800.00, currentPrice: 3245.78, change24h: -1.23, allocation: 25 },
  { id: 3, name: 'Solana', symbol: 'SOL', icon: 'fa-solid fa-sun', iconColor: 'text-purple-500 bg-purple-500/15',
    amount: 156.78, value: 22356.45, avgBuy: 95.00, currentPrice: 142.56, change24h: 5.67, allocation: 15 },
  { id: 4, name: 'Cardano', symbol: 'ADA', icon: 'fa-solid fa-cube', iconColor: 'text-blue-500 bg-blue-500/15',
    amount: 25000, value: 11417.50, avgBuy: 0.38, currentPrice: 0.4567, change24h: 1.23, allocation: 8 },
  { id: 5, name: 'Polygon', symbol: 'MATIC', icon: 'fa-solid fa-shapes', iconColor: 'text-purple-600 bg-purple-600/15',
    amount: 8500, value: 5525.00, avgBuy: 0.55, currentPrice: 0.65, change24h: 3.45, allocation: 4 },
  { id: 6, name: 'Dogecoin', symbol: 'DOGE', icon: 'fa-solid fa-dog', iconColor: 'text-yellow-500 bg-yellow-500/15',
    amount: 35000, value: 4319.00, avgBuy: 0.08, currentPrice: 0.1234, change24h: 8.45, allocation: 3 },
];

// Chart colors
const COLORS = ['hsl(38, 92%, 50%)', 'hsl(240, 60%, 60%)', 'hsl(270, 76%, 65%)', 'hsl(199, 89%, 48%)', 'hsl(280, 60%, 60%)', 'hsl(45, 92%, 50%)'];

// Generate portfolio history data
const generateHistoryData = () => {
  const data = [];
  const now = new Date();
  let value = 150000;
  for (let i = 30; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    value = value * (1 + (Math.random() - 0.48) * 0.03);
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: value,
    });
  }
  return data;
};

const historyData = generateHistoryData();

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-base font-semibold font-mono">
          ${payload[0].value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>
    );
  }
  return null;
};

export const Portfolio = () => {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState('1M');
  const totalValue = holdings.reduce((sum, h) => sum + h.value, 0);
  const totalProfit = holdings.reduce((sum, h) => sum + (h.value - h.amount * h.avgBuy), 0);
  const profitPercentage = (totalProfit / (totalValue - totalProfit)) * 100;

  return (
    <div className="p-4 lg:p-6 space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">{t('portfolio')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('managePortfolio')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <i className="fa-solid fa-download mr-2 text-xs"></i>
            {t('exportCsv')}
          </Button>
          <Button size="sm">
            <i className="fa-solid fa-plus mr-2 text-xs"></i>
            {t('create')}
          </Button>
        </div>
      </div>

      {/* Portfolio Value Card */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Value Info */}
            <div>
              <p className="text-sm text-muted-foreground">{t('portfolioValue')}</p>
              <h2 className="text-3xl sm:text-4xl font-semibold font-mono mt-1">
                ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </h2>
              <div className="flex items-center gap-3 mt-2">
                <Badge variant={totalProfit >= 0 ? 'gain' : 'loss'} className="text-sm px-3 py-1">
                  {totalProfit >= 0 && <i className="fa-solid fa-arrow-up mr-1"></i>}
                  {totalProfit < 0 && <i className="fa-solid fa-arrow-down mr-1"></i>}
                  ${Math.abs(totalProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })} ({profitPercentage.toFixed(2)}%)
                </Badge>
                <span className="text-sm text-muted-foreground">{t('performance')}</span>
              </div>
            </div>

            {/* Time Range Tabs */}
            <Tabs value={timeRange} onValueChange={setTimeRange}>
              <TabsList className="h-8">
                {['24H', '7D', '1M', '3M', '1Y', 'ALL'].map((range) => (
                  <TabsTrigger key={range} value={range} className="text-xs px-3 h-7">
                    {range}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Portfolio Chart */}
          <div className="h-[200px] sm:h-[250px] mt-6">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={historyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} dy={10} interval="preserveStartEnd" />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} width={50} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#portfolioGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Holdings & Allocation */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
        {/* Holdings List */}
        <Card className="xl:col-span-2">
          <CardHeader className="pb-3 px-4 sm:px-6">
            <CardTitle className="text-base">{t('yourHoldings')}</CardTitle>
          </CardHeader>
          <CardContent className="px-0 sm:px-2">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="text-left font-medium py-3 px-4">Asset</th>
                    <th className="text-right font-medium py-3 px-4 hidden sm:table-cell">Price</th>
                    <th className="text-right font-medium py-3 px-4 hidden md:table-cell">24h</th>
                    <th className="text-right font-medium py-3 px-4">Holdings</th>
                    <th className="text-right font-medium py-3 px-4">Value</th>
                    <th className="text-right font-medium py-3 px-4 hidden lg:table-cell">P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {holdings.map((holding) => {
                    const profit = holding.value - (holding.amount * holding.avgBuy);
                    const profitPct = (profit / (holding.amount * holding.avgBuy)) * 100;
                    return (
                      <tr key={holding.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", holding.iconColor)}>
                              <i className={cn(holding.icon, "text-sm")}></i>
                            </div>
                            <div>
                              <p className="font-medium text-sm">{holding.name}</p>
                              <p className="text-xs text-muted-foreground">{holding.symbol}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm hidden sm:table-cell">
                          ${holding.currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: holding.currentPrice < 1 ? 4 : 2 })}
                        </td>
                        <td className="py-3 px-4 text-right hidden md:table-cell">
                          <Badge variant={holding.change24h >= 0 ? 'gain' : 'loss'} className="text-xs">
                            {holding.change24h >= 0 ? '+' : ''}{holding.change24h.toFixed(2)}%
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          {holding.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm font-medium">
                          ${holding.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                        <td className="py-3 px-4 text-right hidden lg:table-cell">
                          <div className={cn("font-mono text-sm", profit >= 0 ? "text-success" : "text-destructive")}>
                            {profit >= 0 ? '+' : ''}{profitPct.toFixed(2)}%
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Allocation Chart */}
        <Card>
          <CardHeader className="pb-2 px-4 sm:px-6">
            <CardTitle className="text-base">{t('allocation')}</CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={holdings}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {holdings.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div className="space-y-2 mt-4">
              {holdings.slice(0, 5).map((holding, index) => (
                <div key={holding.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                    <span className="text-sm">{holding.symbol}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{holding.allocation}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Portfolio;
